/**
 * Google Analytics, loaded late on purpose (2026-09-23).
 *
 * gatsby-ssr.ts writes a tiny inline queue into <head> — `window.dataLayer`,
 * a `gtag()` that pushes onto it, and the `config` call — but never the 176KB
 * gtag.js itself. This file fetches that script once the visitor has shown they
 * are staying (first scroll, tap or key) or after IDLE_MS, whichever comes
 * first. gtag.js drains the queue on arrival, so the first page view and any
 * `whatsapp_click` fired before it loaded are still recorded.
 *
 * Why: with the script fetched on first paint, the live homepage measured a
 * 6.1s mobile LCP with 5.3s of it "render delay"; the identical build without
 * the tag measured 2.5s.
 */
const IDLE_MS = 4000

type GtagWindow = Window & { __qGaId?: string; gtag?: (...args: unknown[]) => void }

let loaded = false

export const loadAnalyticsLater = (): void => {
  const w = window as GtagWindow
  const id = w.__qGaId
  if (!id || loaded) return

  const load = () => {
    if (loaded) return
    loaded = true
    events.forEach((e) => window.removeEventListener(e, load))
    const s = document.createElement('script')
    s.async = true
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`
    document.head.appendChild(s)
  }

  const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const
  events.forEach((e) => window.addEventListener(e, load, { once: true, passive: true }))
  const idle = () => window.setTimeout(load, IDLE_MS)
  if (document.readyState === 'complete') idle()
  else window.addEventListener('load', idle, { once: true })
}

/** A client-side route change. The first page view is sent by the inline `config`. */
export const trackPageView = (path: string): void => {
  const w = window as GtagWindow
  if (!w.__qGaId || typeof w.gtag !== 'function') return
  w.gtag('event', 'page_view', { page_path: path, page_location: window.location.href, page_title: document.title })
}
